<template>
  <div class="float-right">
    <b-modal
        @ok="handleCreateOk"
        ref="create-modal"
        :title="$t('modal.title.add_new_question')"
        ok-title="Save"
        cancel-title="Cancel"
        size="xl">
      <template #modal-header>
        <div class="w-100">
          <h4 class="float-left">{{ $t('modal.title.add_new_question') }}</h4>
          <div class="buttons float-right">
            <b-button type="button" variant="secondary" class="mr-2" @click="hideModal">{{$t('btn.cancel')}}</b-button>
            <b-button type="submit" variant="primary" @click="handleCreateOk">{{$t('btn.save')}}</b-button>
          </div>
        </div>
      </template>
      <div class="create-question">
        <h5 class="mb-3"><strong>{{ $t('title.basic_data') }}</strong></h5>
        <div class="row">
          <div class="col-6">
            <b-form-group
                :label="$t('form.label.type')"
                label-cols="4"
                class="flat"
                id="form-type"
            >
              <div class="w-100">
                <multiselect
                    :options="selectOptions.type"
                    :multiple="false"
                    v-model="data.type"
                    label="text"
                    track-by="value"
                    :placeholder="$t('form.placeholder.type')"
                    :allow-empty="false"
                    :state="validateState('type')"
                >
                </multiselect>
                <p v-if="$v.data.type.$dirty && !$v.data.type.required" class="invalid-feedback invalid-feedback-text">
                  {{ errors.type }}</p>
              </div>
            </b-form-group>
          </div>
          <div v-if="data.type && data.type.value !== 'text'" class="col-6">
              <b-form-group :label="$t('form.question.label.attach_file')" label-cols-sm="4">
                <b-form-file
                    :accept="multimediaConfig ? multimediaConfig['allowed_file_extensions'].map(el => `.${el}`).join(', ') : ''"
                    v-model="file"
                    :placeholder="$t('import.question.file.choose_label')"
                    :drop-placeholder="$t('import.question.file.drop_placeholder')"
                    :browse-text="$t('import.question.file.browse_text')"
                    id="file" ref="file"></b-form-file>
                <p v-if="$v.file.$dirty && !$v.file.required" class="text-danger">{{ $t('error.required') }}</p>
              </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <b-form-group
                :label="$t('form.label.difficulty_level')"
                label-cols="4"
                class="flat">
              <div class="w-100">
                <multiselect
                    :options="selectOptions.difficulty"
                    :multiple="false"
                    v-model="data.difficulty"
                    label="text"
                    track-by="value"
                    :state="validateState('difficulty')"
                    :placeholder="$t('form.placeholder.difficulty_level')"
                >
                </multiselect>
                <p v-if="$v.data.difficulty.$dirty && !$v.data.difficulty.required"
                   class="invalid-feedback invalid-feedback-text">{{ errors.difficulty }}</p>
              </div>
            </b-form-group>
          </div>
          <div class="col-6">

          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <b-form-group
                :label="$t('form.label.categories')"
                label-cols="4"
                class="flat">
              <div class="w-100">
                <multiselect
                    :options="selectOptions.category"
                    :multiple="true"
                    v-model="data.categories"
                    :state="validateState('categories')"
                    label="translated"
                    track-by="id"
                    :placeholder="$t('form.placeholder.categories')">
                </multiselect>
                <p v-if="$v.data.categories.$dirty && !$v.data.categories.required"
                   class="invalid-feedback invalid-feedback-text">{{ errors.categories }}</p>
              </div>
            </b-form-group>
          </div>
          <div class="col-6">
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <b-form-group
                :label="$t('form.label.question_status')"
                label-cols="4"
                class="flat">
              <div class="w-100">
                <multiselect
                    :options="selectOptions.status"
                    :multiple="false"
                    v-model="data.status"
                    :state="validateState('status')"
                    label="text"
                    track-by="value"
                    :placeholder="$t('form.placeholder.question_status')">
                </multiselect>
                <p v-if="$v.data.status.$dirty && !$v.data.status.required"
                   class="invalid-feedback invalid-feedback-text">{{ errors.status }}</p>
              </div>
            </b-form-group>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-5">
            <h5><strong>{{ $t('question.title.content') }}</strong></h5>
          </div>
          <div class="col-6">
            <b-form-group
                id="fieldset-horizontal"
                label-cols-sm="5"
                content-cols-lg="7"
                label-for="input-horizontal"
                :label="$t('form.label.add_new_language')"
            >
              <multiselect
                  v-model="addTranslationLanguage"
                  :options="locale"
                  size="sm"
                  :multiple="false"
                  :placeholder="$t('form.placeholder.select_language')">>
              </multiselect>
            </b-form-group>
          </div>
          <div class="col-1">
            <button class="btn btn-success" :disabled="!addTranslationLanguage" @click="addTranslation">{{
                $t('btn.add')
              }}
            </button>
          </div>
        </div>
        <div class="row mb-4">
          <div class="col-2">
            <p class="label">{{ $t('form.label.correct_answer') }} <br><span
                class="small-label">{{ $t('form.label.common_for_all_translations') }}</span></p>
          </div>
          <div class="col-3">
            <multiselect
                :options="[1,2,3,4]"
                :multiple="false"
                v-model="data.correctAnswer"
                :placeholder="$t('form.placeholder.correct_option')"
                :state="validateState('correctAnswer')">
            </multiselect>
            <p v-if="$v.data.correctAnswer.$dirty && !$v.data.correctAnswer.required"
               class="invalid-feedback invalid-feedback-text">{{ errors.correctAnswer }}</p>
          </div>
        </div>
        <div v-if="clickedSaveButton && data.contentTranslations.length === 0">
          <p class="text-center text-danger">{{ $t('form.label.please_add_at_least') }}</p>
        </div>
        <div v-for="(content,index) in data.contentTranslations" :key="content.id" class="row">
          <contentTranslation
              :ref="`ref-translation-${index}`"
              :content="content"
              :index="index"
              class="mt-2"
              @translation-is-invalid="changeTranslationsValidValue($event, index)"
              @remove-translation="removeTranslation(index, content.lang ? content.lang : content.locale)"
          >
          </contentTranslation>
        </div>
      </div>
      <template #modal-footer>
        <div class="w-100">
          <div class="buttons float-right">
            <b-button type="button" variant="secondary" class="mr-2"  @click="hideModal">{{$t('btn.cancel')}}</b-button>
            <b-button type="submit" variant="primary" @click="handleCreateOk">{{$t('btn.save')}}</b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import {required, requiredIf} from "vuelidate/lib/validators";
import {validationMixin} from "vuelidate";

import ContentTranslation from "./ContentTranslation";
import Multiselect from "vue-multiselect";

//TODO: import / load / get locale the right way
let locale = locale ? locale : "en";

export default {
  components: {ContentTranslation, Multiselect},
  mixins: [validationMixin],
  data() {
    return {
      apiKey: null,
      selectOptions: null,
      addTranslationLanguage: null,
      translationsValidValue: [],
      languages: [],
      clickedSaveButton: false,
      file: null,
      allMultimediaConfig: null,
      data: {
        correctAnswer: null,
        type: null,
        difficulty: null,
        categories: null,
        status: null,
        id: null,
        created_at: null,
        updated_at: null,
        contentTranslations: []
      },
      errors: {
        type: 'Type is a required field!',
        difficulty: 'Difficulty level is a required field!',
        categories: 'Categories is a required field!',
        status: 'Question status is a required field!',
        correctAnswer: 'Correct answer is a required field!'
      }
    };
  },
  validations: {
    data: {
      type: {
        required
      },
      difficulty: {
        required
      },
      categories: {
        required
      },
      status: {
        required
      },
      correctAnswer: {
        required
      }
    },
    file : {
      required: requiredIf(function() {
        return this.data.type && this.data.type.value !== 'text';
      })
    }
  },
  computed: {
    multimediaConfig() {
      let allowedFiles;

      if(this.data.type && this.data.type.value) {
        switch(this.data.type.value) {
          case 'image':
            allowedFiles = this.allMultimediaConfig.image;
            break;
          case 'sound':
            allowedFiles = this.allMultimediaConfig.sound;
            break;
          case 'video':
            allowedFiles = this.allMultimediaConfig.video;
            break;
          default:
            allowedFiles = null;
        }
      }

      return allowedFiles;
    },
    readyToSubmit() {
      if(this.translationsValidValue.length > 0) {
        return this.translationsValidValue.every(v => v === true);
      } else {
        return false;
      }
    },
    locale: function () {
      return this.$store.getters["root/locale"]
          .filter(o => o.domainGroup === process.env.VUE_APP_FRONT_GROUP_NAME)
          .map((d) => d.name);
    },
  },
  mounted() {
    this.getQuestionCofig();
  },
  methods: {
    getQuestionCofig() {
      axios.get('/api/config/configuration/question_import')
          .then(resp => {
            this.allMultimediaConfig = resp.data.value['multimedia_configs'];
          }).catch(function () {
      });
    },
    changeTranslationsValidValue(value, index) {
      this.translationsValidValue.splice(index, 1, !value)
    },
    validateState(entry) {
      if (entry.constructor.name === 'Array') {
        switch (entry.length) {
          case 3:
            entry = this.$v.data[entry[0]][entry[1]][entry[2]];
            break;
          case 2:
            entry = this.$v.data[entry[0]][entry[1]];
            break;
        }
      } else {
        entry = this.$v.data[entry];
      }

      const {$dirty, $error} = entry;
      return $dirty ? !$error : null;
    },
    addTranslation() {
      if (this.addTranslationLanguage !== null) {
        this.data.contentTranslations.push({
          index: this.data.contentTranslations.length + 1,
          lang: this.addTranslationLanguage,
          translationContent: {}
        });
        const index = this.languages.indexOf(this.addTranslationLanguage);
        if (index > -1) {
          this.languages.splice(index, 1);
        }
        this.addTranslationLanguage = null;

        this.translationsValidValue.push(false);
      }
    },
    removeTranslation(index, lang) {
      this.data.contentTranslations.splice(index, 1);
      this.languages.push(lang);
      this.translationsValidValue.splice(index, 1);
    },
    openModal() {


      this.$emit('open-create-modal');

      this.resetForm();

      this.languages = this.selectOptions.lang;

      this.addTranslationLanguage = null;
      this.data = {
        type: null,
        difficulty: null,
        categories: null,
        status: null,
        id: null,
        created_at: null,
        updated_at: null,
        contentTranslations: [],
      };
      this.$refs['create-modal'].show()
    },
    handleCreateOk(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.handleCreateSubmit()
    },
    handleCreateSubmit() {
      this.clickedSaveButton = true;

      this.$v.$touch();

      this.data.contentTranslations.forEach((element,index) => {
        this.$refs[`ref-translation-${index}`][0].touch();
      });

      let isValid = this.$v.$invalid === false;

      if (isValid && this.readyToSubmit) {
        let formData = new FormData();

        let data = {
          difficultyLevel: this.data.difficulty.value,
          categories: this.data.categories,
          contentTranslations: [],
          type: this.data.type.value,
          status: this.data.status.value,
          correctAnswer: this.data.correctAnswer
        };
        let categories = [];
        for (let i in this.data.categories) {
          categories.push({"id": this.data.categories[i].id});
        }
        data.categories = categories;
        let contentTranslations = [];
        for (let i in this.data.contentTranslations) {
          let t = this.$refs['ref-translation-' + i][0].getData();
          contentTranslations.push(t);
        }

        data.contentTranslations = contentTranslations;

        formData.append('data', JSON.stringify(data));
        formData.append('file', this.file);

        axios.post('/api/question/question', formData, { headers: {
              'Content-Type': 'multipart/form-data' }})
          .then(() => {
            setTimeout(() => {
              this.$parent.$emit('refreshGrid')
              this.hideModal();
            }, 1000);
          }).catch(function () {
        });
      }
    },
    resetProperties() {
      this.addTranslationLanguage = null;
      this.translationsValidValue = [];
      this.languages = [];
      this.clickedSaveButton = false;
      this.data = {
        correctAnswer: null,
        type: null,
        difficulty: null,
        categories: null,
        status: null,
        id: null,
        created_at: null,
        updated_at: null,
        contentTranslations: []
      }
    },
    resetForm() {
      this.$v.$reset();
    },
    hideModal() {
      this.resetProperties();

      this.$refs['create-modal'].hide();
    }
  }
}
</script>
